/**
 * 체크아웃 에러 처리 유틸리티
 *
 * @see #135 - CheckoutPage handleProceedToPayment 함수 복잡도 개선
 */

import { ApiClientError } from '@/lib/api/client';

/**
 * 체크아웃 에러 번역 인터페이스
 */
export interface CheckoutErrorTranslations {
  orderFailed?: string;
  networkError?: string;
  badRequest?: string;
  unauthorized?: string;
  notFound?: string;
  serverError?: string;
  validationError?: string;
}

/**
 * HTTP 상태 코드별 번역 키 맵
 */
const HTTP_ERROR_KEYS: Record<number, keyof CheckoutErrorTranslations> = {
  400: 'badRequest',
  401: 'unauthorized',
  404: 'notFound',
  500: 'serverError',
};

/**
 * API 에러를 사용자 친화적인 메시지로 변환
 *
 * @param error 발생한 에러
 * @param t 번역 객체
 * @returns 사용자에게 표시할 에러 메시지
 */
export function getCheckoutErrorMessage(
  error: unknown,
  t: CheckoutErrorTranslations
): string {
  const defaultMessage = t.orderFailed || 'Failed to process order';

  if (error instanceof ApiClientError) {
    // 422: 서버 유효성 검증 메시지 우선
    if (error.status === 422) {
      return error.isServerMessage ? error.message : (t.validationError || 'Please check your input');
    }

    // 서버가 구체적 메시지를 보낸 경우 → 서버 메시지 우선 (4xx/5xx 모두)
    if (error.isServerMessage) {
      return error.message;
    }

    // 서버 메시지 없는 경우 → 번역된 메시지 fallback
    const errorKey = HTTP_ERROR_KEYS[error.status];
    if (errorKey && t[errorKey]) {
      return t[errorKey] as string;
    }

    // 기타 (번역 키 없는 경우) — 클라이언트 생성 메시지 fallback (timeout, network 등)
    return error.message || defaultMessage;
  }

  // 네트워크 연결 오류
  if (error instanceof TypeError && typeof navigator !== 'undefined' && !navigator.onLine) {
    return t.networkError || 'Please check your network connection';
  }

  return defaultMessage;
}
